<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCurrency } from 'dashboard/composables/useCurrency';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import MetricHelp from '../MetricHelp.vue';

const props = defineProps({
  data: {
    type: Object,
    default: null,
  },
});

const { locale } = useI18n();
const browserLocale = computed(() => locale.value.replace('_', '-'));
const { currency } = useCurrency();

// Estado
const sortColumn = ref('total_value');
const sortDirection = ref('desc');

// Computed
const stages = computed(() => {
  if (!props.data?.stages) return [];

  const stagesList = [...props.data.stages];

  stagesList.sort((a, b) => {
    const aValue = a[sortColumn.value];
    const bValue = b[sortColumn.value];

    if (sortColumn.value === 'average_time') {
      const aSeconds = aValue?.seconds || 0;
      const bSeconds = bValue?.seconds || 0;
      return sortDirection.value === 'asc'
        ? aSeconds - bSeconds
        : bSeconds - aSeconds;
    }

    if (sortDirection.value === 'asc') {
      return aValue > bValue ? 1 : -1;
    }
    return aValue < bValue ? 1 : -1;
  });

  return stagesList;
});

// Métodos
const formatCurrency = value => {
  return new Intl.NumberFormat(browserLocale.value, {
    style: 'currency',
    currency: currency.value,
  }).format(value);
};

const sortBy = column => {
  if (sortColumn.value === column) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortColumn.value = column;
    sortDirection.value = 'desc';
  }
};

const getSortIcon = column => {
  if (sortColumn.value !== column) return 'i-lucide-arrow-down-up';
  return sortDirection.value === 'asc'
    ? 'i-lucide-arrow-up'
    : 'i-lucide-arrow-down';
};
</script>

<template>
  <div
    class="bg-white dark:bg-n-slate-2 border border-n-slate-6 rounded-lg overflow-hidden"
  >
    <div class="px-4 py-3 border-b border-n-slate-6">
      <h3 class="text-lg font-semibold text-n-slate-12">
        {{ $t('KANBAN_REPORTS.TABLES.STAGES_TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11 mt-1">
        {{ $t('KANBAN_REPORTS.TABLES.STAGES_DESCRIPTION') }}
      </p>
    </div>

    <div class="overflow-x-auto">
      <table class="w-full">
        <thead class="bg-n-slate-1 dark:bg-n-slate-3">
          <tr>
            <th
              class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider cursor-pointer hover:bg-n-slate-2 dark:hover:bg-n-slate-4 transition-colors"
              @click="sortBy('stage_name')"
            >
              <div class="flex items-center gap-2">
                {{ $t('KANBAN_REPORTS.TABLES.STAGE') }}
                <Icon :icon="getSortIcon('stage_name')" class="size-3.5" />
              </div>
            </th>
            <th
              class="px-4 py-3 text-right text-xs font-medium text-n-slate-11 uppercase tracking-wider cursor-pointer hover:bg-n-slate-2 dark:hover:bg-n-slate-4 transition-colors"
              @click="sortBy('total_cards')"
            >
              <div class="flex items-center justify-end gap-2">
                {{ $t('KANBAN_REPORTS.TABLES.TOTAL_CARDS') }}
                <Icon :icon="getSortIcon('total_cards')" class="size-3.5" />
              </div>
            </th>
            <th
              class="px-4 py-3 text-right text-xs font-medium text-n-slate-11 uppercase tracking-wider cursor-pointer hover:bg-n-slate-2 dark:hover:bg-n-slate-4 transition-colors"
              @click="sortBy('total_value')"
            >
              <div class="flex items-center justify-end gap-2">
                {{ $t('KANBAN_REPORTS.TABLES.TOTAL_VALUE') }}
                <Icon :icon="getSortIcon('total_value')" class="size-3.5" />
              </div>
            </th>
            <th
              class="px-4 py-3 text-right text-xs font-medium text-n-slate-11 uppercase tracking-wider cursor-pointer hover:bg-n-slate-2 dark:hover:bg-n-slate-4 transition-colors"
              @click="sortBy('average_time')"
            >
              <div class="flex items-center justify-end gap-2">
                {{ $t('KANBAN_REPORTS.TABLES.AVG_TIME') }}
                <Icon :icon="getSortIcon('average_time')" class="size-3.5" />
              </div>
            </th>
            <th
              class="px-4 py-3 text-right text-xs font-medium text-n-slate-11 uppercase tracking-wider cursor-pointer hover:bg-n-slate-2 dark:hover:bg-n-slate-4 transition-colors"
              @click="sortBy('conversion_rate')"
            >
              <div class="flex items-center justify-end gap-2">
                {{ $t('CRM_METRICS.WIN_RATE.NAME') }}
                <MetricHelp metric-key="WIN_RATE" />
                <Icon :icon="getSortIcon('conversion_rate')" class="size-3.5" />
              </div>
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-slate-6">
          <tr
            v-for="stage in stages"
            :key="stage.stage_name"
            class="hover:bg-n-slate-1 dark:hover:bg-n-slate-3 transition-colors"
          >
            <td class="px-4 py-3">
              <span class="text-sm font-medium text-n-slate-12">
                {{ stage.stage_name }}
              </span>
            </td>
            <td class="px-4 py-3 text-right text-sm text-n-slate-12">
              {{ stage.total_cards }}
            </td>
            <td class="px-4 py-3 text-right text-sm font-medium text-green-600">
              {{ formatCurrency(stage.total_value) }}
            </td>
            <td class="px-4 py-3 text-right text-sm text-n-slate-12">
              {{ stage.average_time?.formatted || '0s' }}
            </td>
            <td class="px-4 py-3 text-right text-sm font-medium text-blue-600">
              {{ stage.conversion_rate }}%
            </td>
          </tr>

          <tr v-if="stages.length === 0">
            <td
              colspan="5"
              class="px-4 py-8 text-center text-sm text-n-slate-11"
            >
              {{ $t('KANBAN_REPORTS.TABLES.NO_DATA') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
